import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../shared/Header";
import Footer from "../shared/Footer";
import { AnimatePresence, motion } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ExistingPatient from "./ExistingPatient";
import { Button, message } from "antd";
import config from "../config";
import { generateOneTimeToken } from "../utils/authToken";

function Login() {
  const [captcha, setCaptcha] = useState("");
  const [userCaptcha, setUserCaptcha] = useState("");
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  const [showOTPModal, setShowOTPModal] = useState(false);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [timer, setTimer] = useState(120);
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const inputRefs = useRef([]);

  const generateOTP = useCallback(() => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }, []);

  const generateCaptcha = useCallback(() => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let randomCaptcha = "";
    for (let i = 0; i < 6; i++) {
      randomCaptcha += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptcha(randomCaptcha);
    setUserCaptcha("");
    setIsCaptchaValid(false);
    drawCaptcha(randomCaptcha);
  }, []);

  const drawCaptcha = (captchaText) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#f0f0f0";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = "italic bold 30px 'Courier New', monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    for (let i = 0; i < 150; i++) {
      ctx.fillStyle = `rgba(0, 0, 0, ${Math.random() * 0.3})`;
      ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 1, 1);
    }

    for (let i = 0; i < captchaText.length; i++) {
      const x = 20 + i * 20;
      const y = 20 + Math.random() * 5;
      const angle = (Math.random() - 0.5) * 0.5;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.fillStyle = `rgb(${Math.random() * 100}, ${Math.random() * 100}, ${Math.random() * 100})`;
      ctx.fillText(captchaText[i], 0, 0);
      ctx.restore();
    }
  };

  const storeOtpInSession = (otpValue, mobile) => {
    const otpData = {
      otp: otpValue,
      mobile: mobile,
      timestamp: Date.now(),
      expiryTime: Date.now() + 2 * 60 * 1000,
    };
    sessionStorage.setItem("loginOtp", JSON.stringify(otpData));
  };

  const clearOtpFromSession = () => {
    sessionStorage.removeItem("loginOtp");
    setGeneratedOtp("");
  };

  const checkExistingOtp = useCallback(() => {
    const storedOtpData = sessionStorage.getItem("loginOtp");
    if (storedOtpData) {
      const otpData = JSON.parse(storedOtpData);
      const currentTime = Date.now();
      if (currentTime < otpData.expiryTime) {
        setGeneratedOtp(otpData.otp);
        setMobileNumber(otpData.mobile);
        const remainingTime = Math.ceil((otpData.expiryTime - currentTime) / 1000);
        setTimer(remainingTime);
        setShowOTPModal(true);
      } else {
        clearOtpFromSession();
      }
    }
  }, []);

  const validateOtp = (enteredOtp) => {
    const storedOtpData = sessionStorage.getItem("loginOtp");
    if (!storedOtpData) return { valid: false, message: "No OTP found. Please request a new one." };

    const otpData = JSON.parse(storedOtpData);
    const currentTime = Date.now();

    if (currentTime > otpData.expiryTime) {
      clearOtpFromSession();
      return { valid: false, message: "OTP has expired. Please request a new one." };
    }

    if (enteredOtp === otpData.otp) {
      return { valid: true, message: "OTP verified successfully!" };
    } else {
      return { valid: false, message: "Invalid OTP. Please try again." };
    }
  };

  useEffect(() => {
    generateCaptcha();
    checkExistingOtp();
  }, [generateCaptcha, checkExistingOtp]);

  useEffect(() => {
    if (showOTPModal && timer > 0) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    } else if (timer === 0) {
      clearOtpFromSession();
    }
  }, [showOTPModal, timer]);

  const handleCaptchaChange = (e) => {
    const input = e.target.value;
    setUserCaptcha(input);
    setIsCaptchaValid(input === captcha);
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) {
      setMobileNumber(value);
    }
  };

  const handleGetOTPClick = async () => {
    
    // Basic validation
    if (!mobileNumber || mobileNumber.length !== 10 ) {
      toast.error("Invalid Mobile Number.");
      return;
    }
    if (!userCaptcha) {
      toast.error("Fill the Captcha.");
      return;
    }
if (captcha != userCaptcha) {
      toast.error("Invalid  Captcha.");
      return;
    }
    if (config.DEVELOPER_MODE) {
      // Directly set OTP to 123456 in developer mode
      const devOtp = "123456";
      setGeneratedOtp(devOtp);
      storeOtpInSession(devOtp, mobileNumber);

      setShowOTPModal(true);
      setTimer(120);
      setOtp(Array(6).fill(""));
      setTimeout(() => inputRefs.current[0]?.focus(), 100);

       message.success(`A new OTP has been sent to your mobile number ${mobileNumber}`);
      return;
    }

    // Normal OTP flow
    const newOtp = generateOTP();
    setGeneratedOtp(newOtp);
    storeOtpInSession(newOtp, mobileNumber);

    setShowOTPModal(true);
    setTimer(120);
    setOtp(Array(6).fill(""));
    setTimeout(() => inputRefs.current[0]?.focus(), 100);

   // console.log("Generated OTP:", newOtp);

    try {
      const tokeng = generateOneTimeToken();
      const response = await fetch(`${config.API_BASE_URL}/SendOtp`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Key": tokeng },
        body: JSON.stringify({ otp: newOtp, contactNumber: mobileNumber }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`OTP has been sent to your mobile number ${mobileNumber}`);
      } else {
        console.error("Send OTP failed:", data);
        toast.error("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error("An error occurred while sending OTP.");
    }
  };



  const handleResendOtp = () => {
    clearOtpFromSession();
    const newOtp = generateOTP();
    // const newOtp = "123456";

    setGeneratedOtp(newOtp);
    storeOtpInSession(newOtp, mobileNumber);
    setTimer(120);
    setOtp(Array(6).fill(""));
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
    message.success(`A new OTP has been sent to your mobile number ${mobileNumber}`);
  };

  const handleOtpChange = (value, index) => {
    if (value.length > 1) value = value.slice(-1);
    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (e.key === "Enter" && timer !== 0) {
      handleVerifyOtp();
    }

    // You can retain any other key handling here
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

  };

  const handleVerifyOtp = () => {
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== 6) {
      toast.error("Please enter the complete 6-digit OTP.");
      return;
    }

    const validation = validateOtp(enteredOtp);

    if (validation.valid) {
      sessionStorage.setItem("loginMobile", mobileNumber);
      clearOtpFromSession();
      message.success(validation.message);
      setShowOTPModal(false);
      setShowPatientModal(false);

      // Check if selectedDoctor is in session storage
      const selectedDoctor = sessionStorage.getItem("selectedDoctor");
      if (selectedDoctor) {
        navigate("/add-patient");
      } else {
        navigate("/");
      }
    } else {
      toast.error(validation.message);
      if (validation.message.includes("expired")) {
        setShowOTPModal(false);
      }
    }
  };





  return (
    <div className="page">
      <ToastContainer position="top-right" autoClose={1500} />
      <div className="page-main">
        <Header />
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-12 col-sm-10 col-md-8 col-lg-5">
              <div className="card shadow-sm border-0 rounded-4">
                <div className="card-header bg-primary text-white text-center rounded-top-4">
                  <h4 className="mb-0">Patient Login</h4>
                </div>
                <div className="card-body px-4 py-4">
                  <div className="mb-3">
                    <label htmlFor="mobile" className="form-label fw-semibold">Mobile No</label>
                    <input
                      type="text"
                      id="mobile"
                      className="form-control"
                      maxLength={10}
                      value={mobileNumber}
                      onChange={handleMobileChange}
                      placeholder="Enter 10-digit mobile number"
                      autoComplete="tel"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="captcha" className="form-label fw-semibold">Enter Characters Displayed</label>
                    <input
                      type="text"
                      id="captcha"
                      className="form-control"
                      placeholder="Enter CAPTCHA"
                      value={userCaptcha}
                      onChange={handleCaptchaChange}
                      autoComplete="off"
                    />
                    {!isCaptchaValid && userCaptcha && (
                      <small className="text-danger">CAPTCHA does not match.</small>
                    )}
                  </div>

                  <div className="mb-4 text-center">
                    <canvas
                      ref={canvasRef}
                      width="140"
                      height="40"
                      style={{ border: "1px solid #ccc", borderRadius: "6px" }}
                    />
                    <div className="mt-2">
                      <Button onClick={generateCaptcha} size="small">
                        ↻ Refresh
                      </Button>
                    </div>
                  </div>

                  <Button
                    type="primary"
                    block
                    size="large"
                    onClick={handleGetOTPClick}
                  >
                    Get OTP
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* OTP Modal */}
      <AnimatePresence>
        {showOTPModal && (
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)",
              zIndex: 1050,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                background: "#fff",
                padding: "30px",
                borderRadius: "12px",
                width: "95%",
                maxWidth: "600px",
                textAlign: "center",
                position: "relative",
              }}
            >
              <Button
                type="text"
                onClick={() => {
                  setShowOTPModal(false);
                  setOtp(Array(6).fill(""));
                }}
                style={{ position: "absolute", top: 10, right: 10 }}
              >
                ×
              </Button>

              <h5 className="mb-2">Enter OTP</h5>
              <p className="text-muted mb-4">OTP sent to mobile: {mobileNumber}</p>

              <div className="d-flex justify-content-center flex-wrap gap-2 mb-4">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className="form-control text-center"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(e.target.value.replace(/\D/, ""), i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    style={{
                      width: "50px",
                      height: "50px",
                      fontSize: "20px",
                      marginRight: "5px",
                    }}
                  />
                ))}
              </div>

              {timer > 0 ? (
                <p className="text-info">Resend OTP in {Math.floor(timer / 60)}:{("0" + (timer % 60)).slice(-2)}</p>
              ) : (
                <div className="mb-3">
                  <p className="text-danger">OTP has expired!</p>
                  <Button onClick={handleResendOtp}>Resend OTP</Button>
                </div>
              )}

              <div className="mt-4 d-flex justify-content-center gap-2">
                <Button type="primary" onClick={handleVerifyOtp} disabled={timer === 0}>
                  Verify OTP
                </Button>
                <Button danger onClick={() => setShowOTPModal(false)}>
                  Cancel
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPatientModal && (
          <ExistingPatient setShowPatientModal={setShowPatientModal} />
        )}
      </AnimatePresence>
    </div>
  );
}

export default Login;
